import { useEffect, useRef, useState } from 'react';
import { AUTH_RETURN_KEY, readReturnPath } from './returnPath';

/**
 * Marque du fournisseur d'identité, par plugin openid_connect.
 *
 * Le `label` du client Drupal nomme l'organisation propriétaire du compte
 * (« Emerya »), pas le service auquel l'utilisateur se connecte : sur le bouton
 * c'est le fournisseur qu'il faut reconnaître. Les couleurs du « G » sont
 * celles de la marque Google — un logo tiers ne se retokenise pas dans le DS.
 */
const SSO_BRANDS = {
  google: {
    wording: 'Connexion via Google',
    logo: (
      <svg className="h-4 w-4 shrink-0" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
        <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z" />
        <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z" />
        <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z" />
        <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z" />
      </svg>
    ),
  },
};

/**
 * Pourquoi une connexion SSO a échoué, dit à l'utilisateur.
 *
 * openid_connect signale ses refus par le messenger Drupal, que ce front ne
 * rend jamais : sans cela l'utilisateur revient sur ce formulaire sans un mot,
 * et un compte désactivé ressemble à une fausse manœuvre. Le back
 * (openid_connect_extras) renvoie donc ici avec `?sso_error=<code>`, et c'est
 * cette table qui met les mots dessus — le back ne transporte qu'un code, il
 * ne dit ni le nom du compte ni l'adresse e-mail.
 */
const SSO_ERRORS = {
  account_blocked: {
    tone: 'alert-danger',
    message: "Votre compte est désactivé. Veuillez contacter l'administrateur pour le réactiver.",
  },
  cancelled: {
    tone: 'alert-warning',
    message: 'Connexion annulée. Vous pouvez réessayer.',
  },
  failed: {
    tone: 'alert-danger',
    message: "La connexion n'a pas pu aboutir. Réessayez ou contactez l'administrateur.",
  },
};

/**
 * Orbit front-end login page (public route, rendered outside AuthGuard).
 *
 * Why plain React and not a reactive-json YAML page: this screen is served
 * OUTSIDE the reactive-json shell (PublicShell, no ReactiveJsonRoot, no
 * AuthProvider), and both flows are full-page browser transitions handling the
 * session cookie — not data binding. Neither is expressible as an RJ template:
 *  - Password : POST /user/login?_format=json sets the session cookie, then a
 *    full navigation to the return path lets AuthGuard hydrate the session on
 *    reload (no in-SPA data to render here).
 *  - SSO : a full-page redirect to /openid-connect/authorize/{client}. OAuth is
 *    a server-side redirect flow — it cannot run as an in-page fetch — so we
 *    hand the browser to Drupal, which bounces to the identity provider and
 *    back. Providers are listed dynamically from /openid-connect/clients.
 *
 * Déjà connecté : /api/user/info tranche (cookie httpOnly, invisible en JS)
 * et la page redirige vers la home plutôt que d'afficher le formulaire.
 *
 * Both endpoints are same-origin (nginx proxies /user/* and /openid-connect/*
 * to Drupal), so cookies flow without CORS.
 */
export default function LoginPage() {
  const [clients, setClients] = useState([]);
  const [name, setName] = useState('');
  const [pass, setPass] = useState('');
  const [error, setError] = useState('');
  const [errorTone, setErrorTone] = useState('alert-danger');
  const [submitting, setSubmitting] = useState(false);
  // Le cookie de session Drupal est httpOnly : seul le back peut dire si
  // cette page a encore lieu d'être. On attend sa réponse avant de rendre.
  const [checkingSession, setCheckingSession] = useState(true);
  const nameRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/openid-connect/clients', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => { if (!cancelled && Array.isArray(list)) setClients(list); })
      .catch(() => { /* no SSO section if the list can't be loaded */ });
    return () => { cancelled = true; };
  }, []);

  // Retour d'un aller-retour SSO qui a échoué : le back a rangé la raison dans
  // l'URL. On la consomme puis on l'efface de la barre d'adresse — rechargée
  // ou mise en favori, la page ne doit pas re-annoncer un échec passé.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('sso_error');
    if (!code) return;
    const known = SSO_ERRORS[code] || SSO_ERRORS.failed;
    setError(known.message);
    setErrorTone(known.tone);
    params.delete('sso_error');
    const query = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + (query ? `?${query}` : '') + window.location.hash,
    );
  }, []);

  // Déjà connecté : la page de connexion n'a plus lieu d'être, on renvoie
  // vers la home. Même endpoint que AuthGuard, qui fait foi sur la session.
  useEffect(() => {
    let cancelled = false;
    fetch('/api/user/info', { credentials: 'include' })
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setCheckingSession(false);
          return;
        }
        // Un chemin de retour resté en session vient d'une redirection
        // antérieure ; sans passage par le formulaire, il est périmé.
        try { sessionStorage.removeItem(AUTH_RETURN_KEY); } catch { /* sessionStorage indisponible */ }
        // replace() : le bouton « précédent » ne doit pas ramener ici.
        window.location.replace('/');
      })
      .catch(() => { if (!cancelled) setCheckingSession(false); });
    return () => { cancelled = true; };
  }, []);

  // Le champ n'existe qu'une fois la session écartée.
  useEffect(() => { if (!checkingSession) nameRef.current?.focus(); }, [checkingSession]);

  async function handlePasswordSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');
    setErrorTone('alert-danger');
    try {
      const res = await fetch('/user/login?_format=json', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ name, pass }),
      });
      if (res.ok) {
        const returnPath = readReturnPath();
        try { sessionStorage.removeItem(AUTH_RETURN_KEY); } catch (_) {}
        // Full navigation: OrbitShell mounts and AuthGuard hydrates the session.
        window.location.href = returnPath;
        return;
      }
      let message = 'Identifiant ou mot de passe incorrect.';
      try {
        const data = await res.json();
        if (data?.message) message = data.message;
      } catch (_) { /* keep default message */ }
      setError(message);
      setSubmitting(false);
    } catch (_) {
      setError('Connexion impossible. Veuillez réessayer.');
      setSubmitting(false);
    }
  }

  function handleSso(clientId) {
    // Keep AUTH_RETURN_KEY in place: after the SSO round-trip Drupal lands the
    // SPA on /user/{uid} and AuthGuard restores it. Pass it as destination too.
    const dest = encodeURIComponent(readReturnPath());
    window.location.href = `/openid-connect/authorize/${encodeURIComponent(clientId)}?destination=${dest}`;
  }

  if (checkingSession) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-stone-50 p-4">
        <div className="flex flex-col items-center gap-4" role="status" aria-live="polite">
          <div className="w-8 h-8 border-3 border-stone-300 border-t-blue-600 rounded-full animate-spin" aria-hidden="true" />
          <p className="text-sm text-stone-500">Chargement…</p>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-stone-50 p-4">
      <div className="card w-full max-w-sm">
        <div className="card-body flex flex-col gap-5">
          <div className="flex flex-col gap-1 text-center">
            {/* Le logo est décoratif : le titre juste en dessous porte déjà le nom. */}
            <img src="/images/orbit-logo.svg" alt="" className="mx-auto mb-3 h-12 w-auto" />
            <h1 className="text-lg font-semibold text-slate-900">Connexion à Orbit</h1>
            <p className="text-sm text-slate-500">Accédez à votre espace de travail.</p>
          </div>

          {error && (
            <div className={`alert ${errorTone}`} role="alert">{error}</div>
          )}

          <form className="flex flex-col gap-4" onSubmit={handlePasswordSubmit} noValidate>
            <div className="flex flex-col gap-1.5">
              <label className="field-label" htmlFor="login-name">Identifiant</label>
              <input
                id="login-name"
                ref={nameRef}
                className="input"
                type="text"
                name="name"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="field-label" htmlFor="login-pass">Mot de passe</label>
              <input
                id="login-pass"
                className="input"
                type="password"
                name="pass"
                autoComplete="current-password"
                required
                value={pass}
                onChange={(e) => setPass(e.target.value)}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary btn-lg btn-block"
              disabled={submitting}
              aria-busy={submitting}
            >
              {submitting ? 'Connexion…' : 'Se connecter'}
            </button>
          </form>

          {clients.length > 0 && (
            <>
              <div className="flex items-center gap-3" aria-hidden="true">
                <span className="h-px flex-1 bg-slate-200" />
                <span className="text-xs font-medium uppercase tracking-wide text-slate-400">ou</span>
                <span className="h-px flex-1 bg-slate-200" />
              </div>
              <div className="flex flex-col gap-2">
                {clients.map((client) => {
                  const brand = SSO_BRANDS[client.plugin];
                  return (
                    <button
                      key={client.id}
                      type="button"
                      className="btn btn-secondary btn-lg btn-block inline-flex items-center justify-center gap-2"
                      onClick={() => handleSso(client.id)}
                    >
                      {brand?.logo}
                      {brand ? brand.wording : `Se connecter avec ${client.label}`}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
